import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import React, { useEffect, useState } from 'react'

interface BulkEditDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  selectedCategories: string[]
  onBulkEdit: (_data: {
    action: string
    ids: string[]
    data?: Record<string, unknown>
  }) => Promise<void>
  isLoading?: boolean
}

export function BulkEditDialog({
  open,
  onOpenChange,
  selectedCategories,
  onBulkEdit,
  isLoading = false,
}: BulkEditDialogProps) {
  const [action, setAction] = useState<string>('')
  const [newName, setNewName] = useState<string>('')
  const [newStatus, setNewStatus] = useState<string>('')

  useEffect(() => {
    if (!open) {
      // Reset form when dialog closes
      setAction('')
      setNewName('')
      setNewStatus('')
    }
  }, [open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    await onBulkEdit({
      action,
      ids: selectedCategories,
      data:
        action === 'update_name'
          ? { name: newName }
          : action === 'update_status'
            ? { status: newStatus }
            : undefined,
    })

    onOpenChange(false)
  }

  const isFormValid = () => {
    return action !== ''
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Bulk Edit Categories</DialogTitle>
          <DialogDescription>
            Apply changes to {selectedCategories.length} selected categor
            {selectedCategories.length > 1 ? 'ies' : 'y'}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="action" className="text-right">
                Action
              </Label>
              <Select value={action} onValueChange={setAction}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activate">Activate</SelectItem>
                  <SelectItem value="deactivate">Deactivate</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {action === 'update_name' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  New Name
                </Label>
                <Input
                  id="name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="col-span-3"
                  placeholder="Enter new name"
                />
              </div>
            )}

            {action === 'update_status' && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="status" className="text-right">
                  New Status
                </Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isFormValid() || isLoading}>
              {isLoading
                ? 'Processing...'
                : `Apply to ${selectedCategories.length} categor${selectedCategories.length > 1 ? 'ies' : 'y'}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
